import React, { useState } from "react";
import { compose } from "redux";
import { useTranslation } from "react-i18next";
import {
  Button,
  Icon,
  TextareaAutosize,
  Typography,
  useMediaQuery,
  useTheme,
  TextField,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";

// HOCs
import withUser, { WithUserProps } from "../../../auth/hoc/withUser";
import withRecords, { WithRecordsProps } from "../../hoc/withRecords";
import withConclusion, { WithConclusionProps } from "../../hoc/withConclusion";
import withInterpretation, {
  WithInterpretationProps,
} from "../../hoc/withInterpretation";

// Hooks
import usePrevious from "../../../../hooks/usePrevious";

// Constants
import { CONCLUSION_MAX_LENGTH } from "../../constants";
import { RecordStatusEnum } from "../../../doctor-stats/models";

// Utils
import Utils from "../../../../utils/Utils";

// Styles
import useStyles from "./styles";

/**
 * @param props
 */
function ConclusionSection(
  props: WithConclusionProps &
    WithUserProps &
    WithRecordsProps &
    WithInterpretationProps
) {
  const {
    conclusion,
    updateConclusion,
    updatingConclusion,
    user,
    currentRecord,
    isConclusionEditing,
    toggleConclusionEditing,
    interpretation,
    isInterpretationUpdated,
    isInterpretationUpdating,
    updateInterpretation,
  } = props;
  const theme = useTheme();
  const styles = useStyles();
  const [isTextAreaDisabled, setIsTextAreaDisabled] = useState(true);
  const [t] = useTranslation("common");
  const textAreaRef = React.useRef<HTMLTextAreaElement>(null);
  const autoCompleteRef = React.useRef();
  const prevUpdating = usePrevious(updatingConclusion);
  const prevConclusion = usePrevious(conclusion);
  const [value, setValue] = React.useState(conclusion);
  const [interpretationValue, setInterpretationValue] =
    React.useState(interpretation);
  const [saveIconShown, setSaveIconShown] = React.useState(false);
  const matches = useMediaQuery(theme.breakpoints.down("sm"));
  const [isEditInterpretation, setIsEditInterpretation] = useState(false);
  const isEditable =
    !Utils.isCompanyMaster(user) &&
    currentRecord!.status !== RecordStatusEnum.APPROVED;

  React.useEffect(() => {
    setInterpretationValue(interpretation);
  }, [interpretation]);

  React.useEffect(() => {
    setValue(conclusion);
  }, [conclusion]);

  React.useEffect(() => {
    if (conclusionWasUpdated()) {
      setSaveIconShown(true);

      const timeoutId = setTimeout(() => {
        setSaveIconShown(false);

        clearTimeout(timeoutId);
      }, 2000);
    }
    // eslint-disable-next-line
  }, [updatingConclusion]);

  function conclusionWasUpdated() {
    return (
      typeof prevUpdating !== "undefined" &&
      prevUpdating !== updatingConclusion &&
      updatingConclusion === false &&
      conclusion !== prevConclusion
    );
  }

  function conclusionChanged() {
    return value.trim() !== conclusion;
  }

  function handleEditButtonClick() {
    setIsTextAreaDisabled(false);
    if (!isConclusionEditing) toggleConclusionEditing();

    if (textAreaRef.current !== null) {
      textAreaRef.current.focus();
    }
  }

  function handleEditInterpretationButtonClick() {
    setIsEditInterpretation(true);
    if (!isConclusionEditing) toggleConclusionEditing();

    if (textAreaRef.current !== null) {
      textAreaRef.current.focus();
    }
  }

  function handleSaveButtonClick() {
    setIsTextAreaDisabled(true);
    if (!isEditInterpretation) toggleConclusionEditing();

    if (conclusionChanged()) {
      updateConclusion(value);
    }
  }

  function handleCancelButtonClick() {
    setIsTextAreaDisabled(true);
    if (!isEditInterpretation) toggleConclusionEditing();

    setValue(conclusion);
  }

  function handleCancelInterpretationButtonClick() {
    setIsEditInterpretation(false);
    if (isTextAreaDisabled) toggleConclusionEditing();
    setInterpretationValue(interpretation);
  }

  function handleTextAreaChange({
    target,
  }: React.ChangeEvent<HTMLTextAreaElement>) {
    setValue(target.value);
  }

  function handleInterpretationAutocompleteChange(
    event: React.ChangeEvent<{}>,
    value: string | null
  ) {
    if (value !== null) {
      setInterpretationValue(value);
    }
  }

  function handleInterpretationSave() {
    setIsEditInterpretation(false);
    if (isTextAreaDisabled) toggleConclusionEditing();
    updateInterpretation(interpretationValue);
  }

  function getOptions() {
    let options = [
      t("interpretation.2"),
      t("interpretation.3"),
      t("interpretation.4"),
      t("interpretation.5"),
    ];

    const age = currentRecord!.metaData!.age;
    if (age === null || age < 18) {
      options.unshift(t("interpretation.1_teen"));
    } else {
      options.unshift(t("interpretation.1"));
    }

    return options;
  }

  function renderEditButtons() {
    if (isConclusionEditing && !isTextAreaDisabled) {
      return (
        <React.Fragment>
          <Button
            variant="text"
            color="secondary"
            size="small"
            onClick={handleCancelButtonClick}>
            {t("records.cancel")}
          </Button>
          <Button
            variant="text"
            color="primary"
            size="small"
            onClick={handleSaveButtonClick}>
            {t("records.save")}
          </Button>
        </React.Fragment>
      );
    }

    return (
      <Button
        startIcon={<Icon>edit</Icon>}
        variant="text"
        size="small"
        color="primary"
        onClick={handleEditButtonClick}>
        {t("records.edit")}
      </Button>
    );
  }

  function renderEditInterpretationButtons() {
    if (isConclusionEditing && isEditInterpretation) {
      return (
        <React.Fragment>
          <Button
            variant="text"
            color="secondary"
            size="small"
            onClick={handleCancelInterpretationButtonClick}>
            {t("records.cancel")}
          </Button>
          <Button
            variant="text"
            color="primary"
            size="small"
            onClick={handleInterpretationSave}>
            {t("records.save")}
          </Button>
        </React.Fragment>
      );
    }

    return (
      <Button
        startIcon={<Icon>edit</Icon>}
        variant="text"
        size="small"
        color="primary"
        onClick={handleEditInterpretationButtonClick}>
        {t("records.edit")}
      </Button>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.conclusion_container}>
        {saveIconShown && (
          <div className={styles.save_icon_container}>
            <Icon>cloud_done</Icon>
          </div>
        )}
        {isEditable && (
          <div className={styles.edit_icon_container}>
            {renderEditButtons()}
          </div>
        )}
        {isEditable && (
          <div className={styles.length_counter}>
            <Typography component="span" className={styles.length_counter_text}>
              {value.length}/{CONCLUSION_MAX_LENGTH}
            </Typography>
          </div>
        )}
        <TextareaAutosize
          value={value}
          ref={textAreaRef}
          rows={matches ? 5 : 10}
          onChange={handleTextAreaChange}
          maxLength={CONCLUSION_MAX_LENGTH}
          disabled={!isEditable || isTextAreaDisabled}
          className={styles.conclusion_text_area}
        />
      </div>
      <div className={styles.interpretation_container}>
        <Autocomplete
          id="combo-box-demo"
          freeSolo
          ref={autoCompleteRef}
          options={[...getOptions()]}
          getOptionLabel={(option) => option}
          value={interpretationValue}
          disabled={!isEditable || !isEditInterpretation}
          disableClearable
          onChange={handleInterpretationAutocompleteChange}
          onInputChange={(event, value) => {
            if (event && event.type !== "blur") {
              setInterpretationValue(value);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("interpretation.title")}
              variant="outlined"
            />
          )}
        />
        <div className={styles.interpretation_button_container}>
          {renderEditInterpretationButtons()}
        </div>
      </div>
    </div>
  );
}

export default compose(
  withUser,
  withRecords,
  withConclusion,
  withInterpretation
)(ConclusionSection);

